import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Building2,
  CarFront,
  Check,
  CheckCircle2,
  ChevronDown,
  Clock3,
  ClipboardCheck,
  Database,
  Download,
  ExternalLink,
  FileLock2,
  FileText,
  Gavel,
  Landmark,
  Lock,
  Mail,
  MapPin,
  MessagesSquare,
  Menu,
  RefreshCw,
  Scale,
  Search,
  Shield,
  ShieldAlert,
  ShieldCheck,
  X,
  type LucideIcon,
} from "lucide-react";
import { type CSSProperties, type FormEvent, type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import {
  createCheckout,
  createReport,
  downloadReportPdf,
  getCheckoutStatus,
  getReport,
  recoverAccess,
  searchStands,
} from "./api";
import { useDocumentHead } from "./use-document-head";
import { useTurnstileConfig } from "./use-turnstile-site-key";

import { getManagersPanelRows, getManagersPanelSourceLabel } from "./managers-panel-data";
import { faqEntries } from "./content/faq";
import { legalSections } from "./content/legal";
import { billingFaqEntries, pricingGuarantee, pricingPageIntro, pricingProofItems } from "./content/pricing";
import { ReportPendingState } from "./components/ReportPendingState";
import { sourceCards } from "./content/source-cards";
import type {
  CheckoutStatus,
  ReportCategory,
  RiskBand,
  SearchResult,
  Severity,
  SourceStatus,
  StandReport,
} from "../shared/types";
import { formatNif, isValidEmail, normalizeEmail, normalizePortugueseNifSearch } from "../shared/validation";

type TurnstileRenderOptions = {
  sitekey: string;
  action?: string;
  theme?: "auto" | "light" | "dark";
  size?: "normal" | "compact" | "flexible";
  callback?: (token: string) => void;
  "error-callback"?: () => void;
  "expired-callback"?: () => void;
  "timeout-callback"?: () => void;
};

type TurnstileApi = {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const cx = (...classes: Array<string | false | null | undefined>) => classes.filter(Boolean).join(" ");

type ButtonVariant = "primary" | "secondary" | "outline" | "ghost";
type ButtonSize = "sm" | "md" | "lg";

const buttonBase =
  "focus-ring inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-semibold transition disabled:pointer-events-none disabled:opacity-50";

const buttonVariants: Record<ButtonVariant, string> = {
  primary: "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90",
  secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
  outline: "border border-border bg-card text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground",
  ghost: "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
};

const buttonSizes: Record<ButtonSize, string> = {
  sm: "h-9 px-3 text-sm",
  md: "h-10 px-4 text-sm",
  lg: "h-12 px-6 text-base",
};

const getButtonClass = (variant: ButtonVariant = "primary", size: ButtonSize = "md", className?: string) =>
  cx(buttonBase, buttonVariants[variant], buttonSizes[size], className);

type NavLinkProps = {
  href: string;
  navigate: (href: string) => void;
  className?: string;
  children: ReactNode;
  onClick?: () => void;
  "aria-label"?: string;
};

const NavLink = ({ href, navigate: nav, className, children, onClick: extra, ...rest }: NavLinkProps) => (
  <a
    href={href}
    className={className}
    onClick={(e) => {
      e.preventDefault();
      extra?.();
      nav(href);
    }}
    {...rest}
  >
    {children}
  </a>
);

const routeAliases: Record<string, string> = {
  "/directory": "/",
  "/example": "/exemplo",
  "/verificar": "/",
  "/precos": "/pricing",

  "/metodologia": "/methodology",
  "/contacto": "/contact",
  "/contactar": "/contact",
  "/login": "/entrar",
  "/signup": "/registar",
  "/termos": "/legal",
  "/privacidade": "/legal",

};

const headerNavItems = [
  { href: "#como-funciona", label: "Como Funciona" },
  { href: "#verificacoes", label: "O Que Verificamos" },
  { href: "/exemplo", label: "Exemplo" },
  { href: "#precos", label: "Preços" },
  { href: "#faq", label: "FAQ" },
] as const;

const exampleQueries = ["509 442 013", "516 742 930", "501 964 843"] as const;

const trustBadges = ["Fontes públicas", "Evidência datada", "PDF incluído"] as const;

const stats = [
  { value: "1 200+", label: "Stands verificados" },
  { value: "9", label: "Categorias de verificação" },
  { value: "5", label: "Estados de fonte claros" },
  { value: "< 10s", label: "Tempo de geração" },
] as const;

const riskCases = [
  {
    Icon: AlertTriangle,
    tag: "2023-2025",
    title: "Stand vende o mesmo carro duas vezes",
    location: "Setúbal",
    description:
      "Mesmo veículo vendido a dois compradores distintos, com documentação falsa. Ambos perderam o sinal e o carro.",
    loss: "18 000 €",
    victims: "2 famílias",
    iconColor: "text-danger",
    iconBg: "bg-danger/10",
  },
  {
    Icon: Scale,
    tag: "Casos recorrentes",
    title: "Empresa com insolvência ativa",
    location: "Porto e Grande Lisboa",
    description:
      "Compradores entregam sinal ou pagamento total. A empresa entra em insolvência semanas depois e recuperar o valor torna-se difícil.",
    loss: "8 000 €+",
    victims: "por vítima",
    iconColor: "text-warning",
    iconBg: "bg-warning/10",
  },
  {
    Icon: ShieldAlert,
    tag: "Recorrente",
    title: "Venda sem licença IMT visível",
    location: "Nacional",
    description:
      "Operação sem autorização clara para intermediação ou venda automóvel. O comprador fica com menos proteção quando surge conflito.",
    loss: "5 000 €+",
    victims: "por vítima",
    iconColor: "text-muted-foreground",
    iconBg: "bg-muted",
  },
] as const;

const howSteps = [
  {
    step: "01",
    Icon: Search,
    title: "Pesquise pelo NIF/NIPC",
    description: "Introduza o NIF/NIPC completo do stand. A pesquisa é gratuita e não requer conta.",
  },
  {
    step: "02",
    Icon: BarChart3,
    title: "Veja o nível de risco",
    description: "Consulte o score, insolvências, obrigações legais, reclamações e sinais públicos críticos.",
  },
  {
    step: "03",
    Icon: FileText,
    title: "Decida com dados reais",
    description: "Desbloqueie o relatório completo com evidência por fonte, recomendações concretas e PDF.",
  },
] as const;

const sourceLabels = [
  "CITIUS",
  "Registo",
  "IMT / ASAE",
  "MJ",
  "Citius",
  "AT / SS",
  "Tribunais",
  "Consumo",
  "Editais",
] as const;

const pricingCards = [
  {
    name: "Pesquisa Gratuita",
    price: "0 €",
    period: "sem custo",
    description: "Veja identidade, score por intervalo e categorias essenciais sem criar conta.",
    cta: "Pesquisar grátis",
    target: "/verificar",
    variant: "outline" as const,
    featured: false,
    disabled: false,
    features: [
      "Score de risco por intervalo",
      "Categorias gratuitas de risco",
      "Identidade e NIF verificados",
      "Sem conta necessária",
    ],
  },
  {
    name: "Relatório Completo",
    price: "19,99 €",
    period: "pagamento único",
    tag: "PDF incluído",
    description: "Todas as categorias com detalhe, recomendações práticas e evidência por fonte.",
    cta: "Obter relatório",
    target: "/verificar",
    variant: "primary" as const,
    featured: true,
    disabled: false,
    features: [
      "Score exato de 0 a 100",
      "Todas as categorias desbloqueadas",
      "Evidência por fonte pública",
      "Recomendações e PDF profissional",
      "Acesso permanente ao relatório",
    ],
  },
] as const;

const comparisonRows = [
  ["Pesquisa de stand", "Incluído", "Incluído"],
  ["Score exato", "Intervalo", "Incluído"],
  ["Evidência por fonte", "Limitado", "Incluído"],
  ["PDF profissional", "Não incluído", "Incluído"],
  ["Relatórios incluídos", "0", "1"],
] as const;


const exampleCheckedAt = "2026-05-20T09:30:00.000Z";

const exampleReport = {
  id: "exemplo",
  paid: true,
  status: "complete",
  score: 64,
  band: "Atenção",
  verdict: "Há sinais a confirmar antes de pagar sinal, mas não existe um bloqueio automático nesta leitura de exemplo.",
  checkedAt: exampleCheckedAt,
  entity: {
    id: "example-stand",
    nif: "509442013",
    name: "AutoVendas do Norte, Lda",
    aliases: ["AutoVendas Norte"],
    address: "Rua da Oficina, 42, 4700-000 Braga",
    district: "Braga",
    cae: "45110",
    activity: "Comércio de veículos automóveis ligeiros",
    ageYears: 8,
    managers: ["Marta Silva", "João Almeida"],
    status: "active",
    sourceUpdatedAt: exampleCheckedAt,
    sourceEvidence: [
      {
        provider: "managers_public_records",
        sourceName: "Publicacoes oficiais e registos judiciais",
        status: "checked",
        severity: "info",
        checkedAt: exampleCheckedAt,
        evidenceUrl: "https://publicacoes.mj.pt/pesquisa.aspx",
        findings: ["Gerentes e sócios identificados via MJ.ENGINO.PT."],
        raw: {
          sociosSource: "mj_engino_api",
          managers: ["Marta Silva", "João Almeida"],
          partners: ["Marta Silva", "João Almeida"],
          management: [
            {
              name: "Marta Silva",
              nif: "123456789",
              role: "Gerente",
              body: "GERÊNCIA",
              since: "2021-03-14",
            },
            {
              name: "João Almeida",
              nif: "987654321",
              role: "Gerente",
              body: "GERÊNCIA",
              since: "2021-03-14",
            },
          ],
          socios: [
            {
              name: "Marta Silva",
              nif: "123456789",
              role: "sócio",
              quotas_eur: 25000,
              quotas_percent: 50,
            },
            {
              name: "João Almeida",
              nif: "987654321",
              role: "sócio",
              quotas_eur: 25000,
              quotas_percent: 50,
            },
          ],
        },
      },
    ],
  },
  categories: [
    {
      id: "company_identity_1",
      sourceId: "company_identity",
      title: "Identidade da empresa",
      summary: "NIF/NIPC, denominação e atividade aparecem consistentes nas fontes de identidade usadas no exemplo.",
      sourceName: "VIES",
      status: "checked",
      severity: "info",
      scoreImpact: 0,
      findings: ["NIF/NIPC 509442013", "CAE 45110: Comércio de veículos automóveis ligeiros", "Morada comercial em Braga"],
      evidenceUrl: "https://ec.europa.eu/taxation_customs/vies/",
      checkedAt: exampleCheckedAt,
      freePreview: true,
    },
    {
      id: "insolvency_per_2",
      sourceId: "insolvency_per",
      title: "PER, PEAP e insolvências",
      summary: "Não foram encontrados anúncios compatíveis na pesquisa pública simulada.",
      sourceName: "CITIUS / Justiça.gov.pt",
      status: "no_data",
      severity: "info",
      scoreImpact: 0,
      findings: ["Sem correspondência por NIF/NIPC neste exemplo", "Confirme sempre no portal original antes de transferir valores"],
      evidenceUrl: "https://justica.gov.pt/Servicos/Consultar-publicidade-do-PER-e-insolvencias",
      checkedAt: exampleCheckedAt,
      freePreview: true,
    },
    {
      id: "public_debts_3",
      sourceId: "public_debts",
      title: "Dívidas fiscais e à Segurança Social",
      summary: "Foi encontrado um sinal fiscal simulado que exige confirmação antes de avançar.",
      sourceName: "Autoridade Tributária / Segurança Social",
      status: "attention",
      severity: "warning",
      scoreImpact: -14,
      findings: ["Sinal fiscal público encontrado em intervalo baixo", "Ausência noutra lista não substitui certidão oficial de não dívida"],
      evidenceUrl: "https://www.portaldasfinancas.gov.pt/at/html/index.html",
      checkedAt: exampleCheckedAt,
      freePreview: true,
    },
    {
      id: "automotive_compliance_4",
      sourceId: "automotive_compliance",
      title: "Atividade automóvel e obrigações do stand",
      summary: "CAE compatível com comércio automóvel; confirme contrato, garantia e identificação completa do vendedor.",
      sourceName: "ASAE e legislação automóvel",
      status: "checked",
      severity: "info",
      scoreImpact: 0,
      findings: ["Atividade compatível com venda automóvel", "Checklist documental recomendada antes de pagar sinal"],
      evidenceUrl: "https://www.asae.gov.pt/perguntas-frequentes1/area-economica/stands-de-automoveis.aspx",
      checkedAt: exampleCheckedAt,
      freePreview: true,
    },
    {
      id: "civil_processes_5",
      sourceId: "civil_processes",
      title: "Lista pública de execuções",
      summary: "Existe uma correspondência simulada que deve ser confirmada pela fonte antes de qualquer pagamento.",
      sourceName: "Lista pública de execuções",
      status: "attention",
      severity: "warning",
      scoreImpact: -12,
      findings: ["Correspondência por nome social semelhante", "Verifique NIF/NIPC e número do processo na fonte oficial"],
      evidenceUrl: "https://justica.gov.pt/Servicos/Consultar-lista-publica-de-execucoes",
      checkedAt: exampleCheckedAt,
      freePreview: false,
    },
    {
      id: "edictal_citations_6",
      sourceId: "edictal_citations",
      title: "Citações editais",
      summary: "Não foram encontrados editais compatíveis na pesquisa textual simulada.",
      sourceName: "Citações editais / Justiça.gov.pt",
      status: "no_data",
      severity: "info",
      scoreImpact: 0,
      findings: ["Pesquisa textual sem correspondências fortes", "Homonímia deve ser confirmada manualmente quando existir sinal"],
      evidenceUrl: "https://justica.gov.pt/Servicos/Consultar-citacao-edital",
      checkedAt: exampleCheckedAt,
      freePreview: false,
    },
    {
      id: "managers_7",
      sourceId: "managers",
      title: "Gerentes e sócios",
      summary: "Gerentes e sócios identificados via MJ.ENGINO.PT neste exemplo.",
      sourceName: "Publicações oficiais e registos judiciais",
      status: "checked",
      severity: "info",
      scoreImpact: 0,
      findings: ["Gerentes e sócios identificados via MJ.ENGINO.PT."],
      evidenceUrl: "https://publicacoes.mj.pt/pesquisa.aspx",
      checkedAt: exampleCheckedAt,
      freePreview: false,
      sourceEvidence: [
        {
          provider: "managers_public_records",
          sourceName: "Publicacoes oficiais e registos judiciais",
          status: "checked",
          severity: "info",
          checkedAt: exampleCheckedAt,
          evidenceUrl: "https://publicacoes.mj.pt/pesquisa.aspx",
          findings: ["Gerentes e sócios identificados via MJ.ENGINO.PT."],
          raw: {
            sociosSource: "mj_engino_api",
            managers: ["Marta Silva", "João Almeida"],
            partners: ["Marta Silva", "João Almeida"],
            management: [
              {
                name: "Marta Silva",
                nif: "123456789",
                role: "Gerente",
                body: "GERÊNCIA",
                since: "2021-03-14",
              },
              {
                name: "João Almeida",
                nif: "987654321",
                role: "Gerente",
                body: "GERÊNCIA",
                since: "2021-03-14",
              },
            ],
            socios: [
              {
                name: "Marta Silva",
                nif: "123456789",
                role: "sócio",
                quotas_eur: 25000,
                quotas_percent: 50,
              },
              {
                name: "João Almeida",
                nif: "987654321",
                role: "sócio",
                quotas_eur: 25000,
                quotas_percent: 50,
              },
            ],
          },
        },
      ],
    },
    {
      id: "web_feedback_8",
      sourceId: "web_feedback",
      title: "Sinais públicos na web",
      summary: "Sem menções relevantes encontradas nesta pesquisa web simulada.",
      sourceName: "Pesquisa web pública",
      status: "no_data",
      severity: "info",
      scoreImpact: 0,
      findings: ["Sem menções públicas detetadas no exemplo"],
      evidenceUrl: "https://standxray.com/methodology",
      checkedAt: exampleCheckedAt,
      freePreview: true,
    },
  ],
  recommendations: [
    {
      severity: "warning",
      title: "Peça certidão de não dívida atualizada",
      body: "O sinal fiscal simulado justifica confirmação documental antes de entregar sinal ou pagar a totalidade.",
    },
    {
      severity: "warning",
      title: "Confirme a execução pública na fonte",
      body: "Valide NIF/NIPC, número do processo e data para excluir homónimos ou registos antigos.",
    },
    {
      severity: "info",
      title: "Guarde tudo por escrito",
      body: "Contrato, garantia, matrícula, quilometragem e condições de devolução devem ficar documentados.",
    },
  ],
} satisfies StandReport;

const homepageFaqEntries: ReadonlyArray<[question: string, answer: string]> = [
  ...faqEntries,
  [
    "Os dados são atualizados em tempo real?",
    "Os dados são consultados nas fontes configuradas no momento da pesquisa ou ao atualizar o relatório. A data de consulta aparece sempre no relatório.",
  ],
  [
    "Preciso de conta para pesquisar?",
    "Não. A pesquisa gratuita não exige conta. Só pedimos email quando desbloqueia um relatório completo para receber o acesso.",
  ],
];

const footerLinkGroups = {
  Produto: [
    { label: "Como Funciona", href: "#como-funciona" },
    { label: "O Que Verificamos", href: "#verificacoes" },
    { label: "Preços", href: "/pricing" },
    { label: "Relatório de exemplo", href: "/exemplo" },
    { label: "Verificar Stand", href: "/verificar" },
  ],
  Recursos: [
    { label: "Metodologia", href: "/methodology" },
    { label: "FAQ", href: "/faq" },
    { label: "Contacto", href: "/contact" },
  ],
  Legal: [{ label: "Termos, privacidade e responsabilidade", href: "/legal" }],
} as const;

const bandStyles: Record<
  RiskBand,
  { badge: string; dot: string; icon: LucideIcon; gauge: string; label: string; surface: string; text: string }
> = {
  Seguro: {
    badge: "border-success/20 bg-success/10 text-success",
    dot: "bg-success",
    icon: ShieldCheck,
    gauge: "var(--success)",
    label: "Confiável",
    surface: "bg-success/10",
    text: "text-success",
  },
  Atenção: {
    badge: "border-warning/20 bg-warning/10 text-warning",
    dot: "bg-warning",
    icon: AlertTriangle,
    gauge: "var(--warning)",
    label: "Cautela",
    surface: "bg-warning/10",
    text: "text-warning",
  },
  "Risco Elevado": {
    badge: "border-danger/20 bg-danger/10 text-danger",
    dot: "bg-danger",
    icon: ShieldAlert,
    gauge: "var(--danger)",
    label: "Risco Elevado",
    surface: "bg-danger/10",
    text: "text-danger",
  },
  "Cautela Extrema": {
    badge: "border-danger/20 bg-danger/10 text-danger",
    dot: "bg-danger",
    icon: ShieldAlert,
    gauge: "var(--danger)",
    label: "Cautela Extrema",
    surface: "bg-danger/10",
    text: "text-danger",
  },
};

const severityStyles: Record<Severity, { badge: string; dot: string; label: string; text: string }> = {
  clear: {
    badge: "border-success/20 bg-success/10 text-success",
    dot: "bg-success",
    label: "Sem alerta",
    text: "text-success",
  },
  info: {
    badge: "border-primary/20 bg-primary/10 text-primary",
    dot: "bg-primary",
    label: "Informativo",
    text: "text-primary",
  },
  warning: {
    badge: "border-warning/20 bg-warning/10 text-warning",
    dot: "bg-warning",
    label: "Atenção",
    text: "text-warning",
  },
  critical: {
    badge: "border-danger/20 bg-danger/10 text-danger",
    dot: "bg-danger",
    label: "Crítico",
    text: "text-danger",
  },
};

const sourceStatusStyles: Record<SourceStatus, { badge: string; dot: string; label: string; description: string }> = {
  checked: {
    badge: "border-primary/20 bg-primary/10 text-primary",
    dot: "bg-primary",
    label: "Consultada",
    description: "A fonte respondeu e devolveu dados úteis para esta secção.",
  },
  no_data: {
    badge: "border-success/20 bg-success/10 text-success",
    dot: "bg-success",
    label: "Sem dados encontrados",
    description: "A consulta foi feita e não encontrou registos correspondentes nesta fonte pública.",
  },
  attention: {
    badge: "border-warning/20 bg-warning/10 text-warning",
    dot: "bg-warning",
    label: "Sinal encontrado",
    description: "A fonte devolveu informação que deve ser confirmada antes de avançar.",
  },
  failed: {
    badge: "border-warning/20 bg-warning/10 text-warning",
    dot: "bg-warning",
    label: "Falha técnica",
    description: "A fonte não ficou disponível; isto não foi tratado como ausência de risco.",
  },
  not_connected: {
    badge: "border-border bg-secondary text-muted-foreground",
    dot: "bg-muted-foreground",
    label: "Ainda não ligada",
    description: "A cobertura está documentada, mas ainda não há consulta automática estável.",
  },
  locked: {
    badge: "border-primary/20 bg-primary/10 text-primary",
    dot: "bg-primary",
    label: "Premium",
    description: "Esta evidência fica disponível no relatório completo.",
  },
};

const categoryIcons: Record<string, LucideIcon> = {
  company_identity: Building2,
  insolvency_per: Scale,
  public_debts: Landmark,
  automotive_compliance: CarFront,
  civil_processes: Gavel,
  edictal_citations: ClipboardCheck,
  web_feedback: MessagesSquare,
};

type CategoryLink = {
  href: string;
  label: string;
};

const findingUrlPattern = / — (https?:\/\/\S+)$/;

const parseFindingLink = (finding: string): CategoryLink | null => {
  const match = finding.match(findingUrlPattern);
  if (!match?.[1]) {
    return null;
  }

  return {
    label: finding.slice(0, finding.length - match[0].length),
    href: match[1],
  };
};

const getCategoryStatusStyle = (category: ReportCategory) => {
  if (category.locked) {
    return sourceStatusStyles.locked;
  }

  if (category.status === "attention" && category.severity === "critical") {
    return {
      ...sourceStatusStyles.attention,
      badge: severityStyles.critical.badge,
      dot: severityStyles.critical.dot,
    };
  }

  return sourceStatusStyles[category.status];
};

const usePath = () => {
  const readPath = () => `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const [path, setPath] = useState(readPath);

  useEffect(() => {
    const handlePopState = () => setPath(readPath());
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const navigate = (href: string) => {
    const isHashOnly = href.startsWith("#");
    const nextHref = isHashOnly ? `${window.location.pathname}${window.location.search}${href}` : href;
    const hash = nextHref.split("#")[1];

    window.history.pushState(null, "", nextHref);
    setPath(readPath());

    if (hash) {
      requestAnimationFrame(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" }));
      return;
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return { path, navigate };
};

export const App = () => {
  const { path, navigate } = usePath();
  const [menuOpen, setMenuOpen] = useState(false);
  useDocumentHead(path);
  const route = path.split(/[?#]/)[0] || "/";
  const visibleRoute = routeAliases[route] ?? route;
  const isReportRoute = visibleRoute.startsWith("/report/");
  const isCheckoutReturnRoute = visibleRoute === "/checkout/return";
  const isCheckoutCancelledRoute = visibleRoute === "/checkout/cancelled";
  const hasKnownRoute =
    visibleRoute === "/" ||
    visibleRoute === "/exemplo" ||
    visibleRoute === "/pricing" ||
    visibleRoute === "/faq" ||
    visibleRoute === "/contact" ||
    visibleRoute === "/legal" ||
    visibleRoute === "/methodology" ||
    visibleRoute === "/entrar" ||
    visibleRoute === "/registar" ||
    isCheckoutReturnRoute ||
    isCheckoutCancelledRoute ||
    isReportRoute;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header menuOpen={menuOpen} navigate={navigate} setMenuOpen={setMenuOpen} />
      <main>
        {visibleRoute === "/" && <HomePage navigate={navigate} />}
        {visibleRoute === "/exemplo" && <ExampleReportPage navigate={navigate} />}
        {visibleRoute === "/pricing" && <PricingPage navigate={navigate} />}
        {visibleRoute === "/faq" && <FaqPage navigate={navigate} />}
        {visibleRoute === "/contact" && <ContactPage />}
        {visibleRoute === "/legal" && <LegalPage />}
        {visibleRoute === "/methodology" && <MethodologyPage navigate={navigate} />}
        {visibleRoute === "/entrar" && <AccountDeferredPage mode="login" navigate={navigate} />}
        {visibleRoute === "/registar" && <AccountDeferredPage mode="register" navigate={navigate} />}
        {isCheckoutReturnRoute && <CheckoutReturnPage navigate={navigate} path={path} />}
        {isCheckoutCancelledRoute && <CheckoutCancelledPage navigate={navigate} path={path} />}
        {isReportRoute && <ReportPage navigate={navigate} path={path} reportId={visibleRoute.replace("/report/", "")} />}
        {!hasKnownRoute && <NotFoundPage navigate={navigate} />}
      </main>
      <Footer navigate={navigate} />
    </div>
  );
};

type NavigationProps = {
  navigate: (href: string) => void;
};

type HeaderProps = NavigationProps & {
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
};

export const Header = ({ menuOpen, navigate, setMenuOpen }: HeaderProps) => {
  const handleNavigate = (href: string) => {
    const isHomeAnchor = href.startsWith("#");
    const isOnHomepage = window.location.pathname === "/" || window.location.pathname === "/verificar";
    const nextHref = isHomeAnchor && !isOnHomepage ? `/${href}` : href;

    setMenuOpen(false);
    navigate(nextHref);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-card/95 backdrop-blur-sm">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 md:px-6">
        <NavLink
          href="/"
          navigate={(h) => handleNavigate(h)}
          className="focus-ring flex items-center gap-2 rounded-md text-left text-foreground"
          aria-label="StandXRAY homepage"
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <ShieldCheck className="h-5 w-5" aria-hidden />
          </span>
          <span className="text-xl font-bold tracking-tight">
            Stand<span className="text-primary">XRAY</span>
          </span>
        </NavLink>

        <nav className="hidden items-center gap-6 md:flex" aria-label="Navegação principal">
          {headerNavItems.map((item) => (
            <NavLink
              href={item.href.startsWith("#") ? `/${item.href}` : item.href}
              navigate={(h) => handleNavigate(item.href)}
              className="focus-ring rounded-md px-1 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              key={item.href}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <NavLink href="/contact" navigate={(h) => handleNavigate(h)} className={getButtonClass("ghost", "sm")}>
            Contacto
          </NavLink>
          <NavLink href="/" navigate={(h) => handleNavigate(h)} className={getButtonClass("primary", "sm")}>
            Verificar Stand
          </NavLink>
        </div>

        <button
          className="focus-ring flex h-10 w-10 items-center justify-center rounded-md text-muted-foreground md:hidden"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? "Fechar menu" : "Abrir menu"}
          aria-expanded={menuOpen}
        >
          {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-border bg-card px-4 pb-4 md:hidden">
          <nav className="flex flex-col gap-1 pt-3" aria-label="Navegação mobile">
            {headerNavItems.map((item) => (
              <NavLink
                href={item.href.startsWith("#") ? `/${item.href}` : item.href}
                navigate={(h) => handleNavigate(item.href)}
                className="focus-ring rounded-md px-3 py-2 text-left text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                key={item.href}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-3 flex flex-col gap-2 border-t border-border pt-3">
            <button className={getButtonClass("outline", "sm")} onClick={() => handleNavigate("/contact")}>
              Contacto
            </button>
            <button className={getButtonClass("primary", "sm")} onClick={() => handleNavigate("/verificar")}>
              Verificar Stand
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export const HomePage = ({ navigate }: NavigationProps) => (
  <>
    <SearchSection navigate={navigate} />
    <StatsBar />
    <RiskCases />
    <HowItWorks navigate={navigate} />
    <WhatWeCheck />
    <PricingSection navigate={navigate} />
    <FAQSection />
    <CTABanner navigate={navigate} />
  </>
);

export const SearchSection = ({ navigate }: NavigationProps) => {
  const turnstileConfig = useTurnstileConfig();
  const { siteKey: turnstileSiteKey, isMisconfigured: turnstileMisconfigured, isLoading: turnstileLoading, required: turnstileRequired } =
    turnstileConfig;
  const [query, setQuery] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [searchError, setSearchError] = useState("");
  const [turnstileError, setTurnstileError] = useState("");
  const [turnstileResetSignal, setTurnstileResetSignal] = useState(0);
  const [turnstileToken, setTurnstileToken] = useState("");
  const queryClient = useQueryClient();
  const resultsRef = useRef<HTMLElement | null>(null);

  const scrollToResults = () => {
    requestAnimationFrame(() => {
      resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  useEffect(() => {
    const initialQuery = new URLSearchParams(window.location.search).get("q");

    if (!initialQuery) {
      return;
    }

    setQuery(initialQuery);
    const nif = normalizePortugueseNifSearch(initialQuery);

    if (nif) {
      setSubmittedQuery(nif);
    } else {
      setSearchError("Introduza um NIF/NIPC português válido com 9 dígitos.");
    }

    scrollToResults();
  }, []);

  const searchQuery = useQuery({
    queryKey: ["search", submittedQuery],
    queryFn: () => searchStands(submittedQuery),
    enabled: submittedQuery.length > 0,
  });

  const reportMutation = useMutation({
    mutationFn: createReport,
    onSuccess: (response) => {
      queryClient.setQueryData(["report", response.report.id, null], response);
      navigate(`/report/${response.report.id}`);
    },
    onSettled: () => {
      setTurnstileToken("");
      setTurnstileResetSignal((value) => value + 1);
    },
  });

  const handleTurnstileError = useCallback(() => {
    setTurnstileToken("");
    setTurnstileError("Confirme a verificação anti-abuso antes de abrir o relatório.");
  }, []);

  const handleTurnstileVerify = useCallback((token: string) => {
    setTurnstileError("");
    setTurnstileToken(token);
  }, []);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedQuery = query.trim();

    if (!normalizedQuery) {
      setSubmittedQuery("");
      setSearchError("");
      setTurnstileError("");
      navigate("/verificar");
      scrollToResults();
      return;
    }

    const nif = normalizePortugueseNifSearch(normalizedQuery);

    if (!nif) {
      setSubmittedQuery("");
      setSearchError("Introduza um NIF/NIPC português válido com 9 dígitos.");
      setTurnstileError("");
      navigate(`/verificar?q=${encodeURIComponent(normalizedQuery)}`);
      scrollToResults();
      return;
    }

    setSearchError("");
    setTurnstileError("");
    setSubmittedQuery(nif);
    navigate(`/verificar?q=${encodeURIComponent(nif)}`);
    scrollToResults();
  };

  const handleUseExample = (value: string) => {
    setQuery(value);
    setSearchError("");
    setTurnstileError("");
  };

  const handleOpenReport = (result: SearchResult) => {
    if (turnstileLoading) {
      setTurnstileError("A carregar a verificação anti-abuso...");
      return;
    }

    if (turnstileMisconfigured) {
      setTurnstileError("A verificação anti-abuso está temporariamente indisponível.");
      return;
    }

    if (turnstileRequired && !turnstileToken) {
      setTurnstileError("Confirme a verificação anti-abuso antes de abrir o relatório.");
      return;
    }

    reportMutation.mutate({ entityId: result.entity.id, turnstileToken: turnstileToken || undefined });
  };

  return (
    <section className="relative overflow-hidden bg-background py-16 md:py-24" aria-labelledby="hero-heading">
      <div className="scan-grid pointer-events-none absolute inset-0 opacity-80" aria-hidden />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-border" aria-hidden />

      <div className="relative mx-auto max-w-6xl px-4 md:px-6">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-accent px-4 py-1.5 text-sm font-medium text-accent-foreground">
            <span className="h-2 w-2 rounded-full bg-success" aria-hidden />
            +1 200 stands verificados em Portugal
          </div>

          <h1
            id="hero-heading"
            className="text-balance text-4xl font-bold tracking-tight text-foreground md:text-5xl lg:text-6xl"
          >
            Faça um raio-X ao <span className="text-primary">stand automóvel</span> antes de comprar
          </h1>

          <p className="mt-5 text-pretty text-lg leading-relaxed text-muted-foreground md:text-xl">
            Em Portugal, muitos stands operam com sinais públicos que só aparecem tarde demais. Introduza o NIF/NIPC e veja
            o risco antes de assinar ou pagar sinal.
          </p>

          <form className="mt-10 flex w-full flex-col gap-3 sm:flex-row" onSubmit={handleSubmit} aria-label="Pesquisar stand">
            <label className="relative flex-1">
              <span className="sr-only">NIF/NIPC do stand automóvel</span>
              <Search
                className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <input
                className="focus-ring h-12 w-full rounded-md border border-input bg-card pl-10 pr-4 text-sm font-medium text-foreground shadow-sm placeholder:text-muted-foreground"
                inputMode="numeric"
                placeholder={`Ex: ${exampleQueries[0]}`}
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setSearchError("");
                  setTurnstileError("");
                }}
              />
            </label>
            <button className={getButtonClass("primary", "lg", "px-6")} disabled={searchQuery.isFetching} type="submit">
              Pesquisar grátis
              <ArrowRight className="h-4 w-4" aria-hidden />
            </button>
          </form>

          <p className="mt-3 text-xs text-muted-foreground">
            Experimente:{" "}
            {exampleQueries.map((value, index) => (
              <button
                className="focus-ring rounded-sm underline underline-offset-2 transition hover:text-primary"
                key={value}
                type="button"
                onClick={() => handleUseExample(value)}
              >
                {value}
                {index < exampleQueries.length - 1 && <span className="text-muted-foreground no-underline">, </span>}
              </button>
            ))}
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            {trustBadges.map((badge) => (
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground" key={badge}>
                <CheckCircle2 className="h-4 w-4 text-success" aria-hidden />
                <span>{badge}</span>
              </div>
            ))}
          </div>

          <button className={getButtonClass("outline", "sm", "mt-6")} onClick={() => navigate("/exemplo")} type="button">
            Ver relatório de exemplo
            <FileText className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <div className="mx-auto mt-14 grid max-w-5xl gap-6 lg:grid-cols-[1.05fr_0.95fr]">
          <section
            ref={resultsRef}
            className="scroll-mt-24 rounded-xl border border-border bg-card shadow-card-soft"
            aria-label="Resultados de pesquisa"
          >
            <div className="border-b border-border px-5 py-4">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Pré-visualização grátis</p>
              <h2 className="mt-1 text-base font-semibold text-card-foreground">Resultado em segundos</h2>
            </div>
            <div className="p-5">
              {!submittedQuery && !searchError && <EmptySearchState />}
              {searchError && <ErrorState message={searchError} compact />}
              {searchQuery.isFetching && <LoadingState label="A cruzar fontes públicas..." compact />}
              {searchQuery.isError && <ErrorState message={searchQuery.error.message} compact />}
              {reportMutation.isError && <ErrorState message={reportMutation.error.message} compact />}
              {turnstileError && <p className="mb-4 text-sm font-semibold text-danger">{turnstileError}</p>}
              {searchQuery.data && searchQuery.data.results.length === 0 && (
                <ErrorState message="Nenhum registo encontrado. Tente pesquisar com o NIF/NIPC completo." compact />
              )}
              {turnstileMisconfigured && (
                <ErrorState
                  message="A verificação anti-abuso está indisponível neste momento. Tente novamente dentro de alguns minutos."
                  compact
                />
              )}
              {searchQuery.data && searchQuery.data.results.length > 0 && !turnstileMisconfigured && (
                <div className="grid gap-4">
                  <TurnstileVerification
                    action="create_report"
                    resetSignal={turnstileResetSignal}
                    siteKey={turnstileSiteKey}
                    onError={handleTurnstileError}
                    onVerify={handleTurnstileVerify}
                  />
                  <div className="grid gap-3">
                    {searchQuery.data.results.map((result) => (
                      <SearchResultCard
                        key={result.entity.id}
                        loading={reportMutation.isPending}
                        result={result}
                        onOpenReport={handleOpenReport}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>

          <MockReportCard />
        </div>
      </div>
    </section>
  );
};

export const EmptySearchState = () => (
  <div className="flex min-h-[300px] flex-col justify-between rounded-lg border border-border bg-secondary/60 p-6">
    <div>
      <div className="mb-5 h-2 w-20 rounded-full bg-primary" />
      <p className="text-2xl font-bold text-foreground">Pesquise por NIF/NIPC.</p>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
        A prévia gratuita mostra identidade, risco por intervalo e as primeiras categorias. O relatório completo
        desbloqueia evidência por fonte, score exato e PDF.
      </p>
    </div>
    <div className="mt-6 grid grid-cols-2 gap-2 text-xs font-semibold">
      <span className="rounded-md bg-card px-3 py-2 text-center text-muted-foreground">VIES</span>
      <span className="rounded-md bg-card px-3 py-2 text-center text-muted-foreground">ASAE</span>
    </div>
  </div>
);

type TurnstileVerificationProps = {
  action: string;
  resetSignal: number;
  siteKey: string;
  onError: () => void;
  onVerify: (token: string) => void;
};

export const TurnstileVerification = ({ action, onError, onVerify, resetSignal, siteKey }: TurnstileVerificationProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!siteKey || !containerRef.current) {
      return;
    }

    const scriptId = "cf-turnstile-script";
    const renderWidget = () => {
      if (!containerRef.current || !window.turnstile || widgetIdRef.current) {
        return;
      }

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        action,
        theme: "auto",
        size: "flexible",
        callback: onVerify,
        "error-callback": onError,
        "expired-callback": onError,
        "timeout-callback": onError,
      });
    };

    const existingScript = document.getElementById(scriptId) as HTMLScriptElement | null;

    if (window.turnstile) {
      renderWidget();
    } else if (existingScript) {
      existingScript.addEventListener("load", renderWidget, { once: true });
    } else {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.addEventListener("load", renderWidget, { once: true });
      document.head.appendChild(script);
    }

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }

      widgetIdRef.current = null;
    };
  }, [action, onError, onVerify, siteKey]);

  useEffect(() => {
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, [resetSignal]);

  if (!siteKey) {
    return null;
  }

  return <div ref={containerRef} className="min-h-[65px] w-full" />;
};

type LoadingStateProps = {
  label: string;
  compact?: boolean;
};

export const LoadingState = ({ compact = false, label }: LoadingStateProps) => (
  <div className={cx("grid place-items-center rounded-lg border border-border bg-card", compact ? "min-h-[300px]" : "min-h-[340px]")}>
    <div className="flex items-center gap-3 font-semibold text-foreground">
      <RefreshCw className="h-5 w-5 animate-spin text-primary" />
      {label}
    </div>
  </div>
);

type ErrorStateProps = {
  message: string;
  compact?: boolean;
};

export const ErrorState = ({ compact = false, message }: ErrorStateProps) => (
  <div
    className={cx(
      "grid place-items-center rounded-lg border border-border bg-card p-8 text-center",
      compact ? "min-h-[300px]" : "min-h-[340px]",
    )}
  >
    <div>
      <AlertTriangle className="mx-auto h-8 w-8 text-warning" />
      <p className="mt-3 text-sm font-semibold text-foreground">{message}</p>
    </div>
  </div>
);

export const MockReportCard = () => (
  <article className="rounded-xl border border-border bg-card shadow-lg" aria-label="Exemplo de relatório">
    <div className="flex items-center justify-between gap-4 border-b border-border px-5 py-4">
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Exemplo de Relatório</p>
        <p className="mt-0.5 font-semibold text-foreground">AutoVendas do Norte, Lda</p>
        <p className="font-mono text-xs text-muted-foreground">NIF: 50X XXX X12</p>
      </div>
      <div className="flex flex-col items-center rounded-lg border border-warning/20 bg-warning/10 px-3 py-2 text-warning">
        <span className="text-2xl font-bold leading-none">38</span>
        <span className="mt-0.5 text-xs font-medium">/100</span>
        <span className="mt-1 text-xs font-semibold">Cautela</span>
      </div>
    </div>

    <div className="divide-y divide-border px-5">
      <RiskItem status="danger" label="Processo de insolvência ativo" />
      <RiskItem status="warning" label="Atividade automóvel requer confirmação documental" />
      <RiskItem status="warning" label="Prestação de contas exige validação manual" />
      <RiskItem status="success" label="Sem dados encontrados nas listas públicas consultadas" />
      <RiskItem status="success" label="Livro de Reclamações disponível apenas como fonte agregada" />
    </div>

    <div className="rounded-b-xl bg-muted px-5 py-3">
      <p className="text-xs text-muted-foreground">Estado por fonte · Gerado em 8s · Dados públicos</p>
    </div>
  </article>
);

type RiskItemProps = {
  status: "success" | "warning" | "danger";
  label: string;
};

export const RiskItem = ({ label, status }: RiskItemProps) => {
  const dotClass = {
    success: "bg-success",
    warning: "bg-warning",
    danger: "bg-danger",
  }[status];

  return (
    <div className="flex items-center gap-3 py-2.5">
      <span className={cx("h-2 w-2 rounded-full", dotClass)} aria-hidden />
      <span className="text-sm text-foreground">{label}</span>
    </div>
  );
};

type SearchResultCardProps = {
  result: SearchResult;
  loading: boolean;
  onOpenReport: (result: SearchResult) => void;
};

export const SearchResultCard = ({ loading, onOpenReport, result }: SearchResultCardProps) => {
  const band = bandStyles[result.preview.scoreBand];
  const Icon = band.icon;

  return (
    <article className="rounded-lg border border-border bg-card p-5 text-card-foreground transition-shadow hover:shadow-md">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className={cx("inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold", band.badge)}>
              <Icon className="h-3.5 w-3.5" />
              {band.label}
            </span>
            <span className="rounded-md bg-secondary px-2.5 py-1 text-xs font-medium text-muted-foreground">
              Score {result.preview.scoreRange}
            </span>
          </div>
          <h3 className="text-lg font-semibold">{result.entity.name}</h3>
          <p className="mt-1 font-mono text-sm text-muted-foreground">NIF {formatNif(result.entity.nif)}</p>
          <p className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="h-4 w-4" />
            {result.entity.district} · {result.entity.activity}
          </p>
        </div>
        <button className={getButtonClass("primary", "sm")} disabled={loading} onClick={() => onOpenReport(result)}>
          Ver Relatório
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {result.preview.categories.map((category) => (
          <div className="rounded-md border border-border bg-secondary/60 p-3" key={category.id}>
            <p className="text-sm font-semibold">{category.title}</p>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">{category.summary}</p>
          </div>
        ))}
      </div>
    </article>
  );
};

export const StatsBar = () => (
  <section className="border-y border-border bg-secondary py-8" aria-label="Estatísticas da plataforma">
    <div className="mx-auto max-w-6xl px-4 md:px-6">
      <dl className="grid grid-cols-2 gap-6 md:grid-cols-4">
        {stats.map((stat) => (
          <div className="text-center" key={stat.label}>
            <dt className="sr-only">{stat.label}</dt>
            <dd className="text-3xl font-bold text-primary">{stat.value}</dd>
            <p className="mt-1 text-sm text-muted-foreground">{stat.label}</p>
          </div>
        ))}
      </dl>
    </div>
  </section>
);

export const RiskCases = () => (
  <section id="casos" className="bg-background py-20 md:py-24" aria-labelledby="casos-heading">
    <div className="mx-auto max-w-6xl px-4 md:px-6">
      <SectionHeader
        eyebrow="Casos Reais"
        title="Não pense que só acontece aos outros"
        body="Estes padrões repetem-se todos os anos. Verificar antes de comprar é uma das poucas proteções que controla."
      />

      <div className="grid gap-6 md:grid-cols-3">
        {riskCases.map((item) => (
          <article className="rounded-xl border border-border bg-card p-6 transition-shadow hover:shadow-md" key={item.title}>
            <div className="mb-4 flex items-start justify-between gap-3">
              <div className={cx("flex h-10 w-10 items-center justify-center rounded-lg", item.iconBg)}>
                <item.Icon className={cx("h-5 w-5", item.iconColor)} aria-hidden />
              </div>
              <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">{item.tag}</span>
            </div>
            <h3 className="font-semibold text-foreground">{item.title}</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">{item.location}</p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{item.description}</p>
            <div className="mt-4 border-t border-border pt-4">
              <p className="text-lg font-bold text-danger">{item.loss}</p>
              <p className="text-xs text-muted-foreground">{item.victims}</p>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

export const HowItWorks = ({ navigate }: NavigationProps) => (
  <section id="como-funciona" className="bg-secondary py-20 md:py-24" aria-labelledby="how-heading">
    <div className="mx-auto max-w-6xl px-4 md:px-6">
      <SectionHeader eyebrow="Como Funciona" title="Verificar é simples. E pode poupar-lhe milhares." />

      <div className="grid gap-8 md:grid-cols-3">
        {howSteps.map((item, index) => (
          <div className="relative flex flex-col gap-4" key={item.step}>
            {index < howSteps.length - 1 && (
              <div className="absolute left-[calc(50%+2.5rem)] top-6 hidden h-px w-full bg-border md:block" aria-hidden />
            )}
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <item.Icon className="h-5 w-5" aria-hidden />
              </div>
              <span className="select-none text-3xl font-bold text-muted-foreground/30">{item.step}</span>
            </div>
            <div>
              <h3 className="font-semibold text-foreground">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.description}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12 text-center">
        <button className={getButtonClass("primary", "lg")} onClick={() => navigate("/verificar")}>
          Verificar Stand Agora
        </button>
        <p className="mt-3 text-sm text-muted-foreground">Pesquisa gratuita. Pague só se precisar do relatório completo.</p>
      </div>
    </div>
  </section>
);

export const WhatWeCheck = () => (
  <section id="verificacoes" className="bg-background py-20 md:py-24" aria-labelledby="checks-heading">
    <div className="mx-auto max-w-6xl px-4 md:px-6">
      <SectionHeader
        eyebrow="O Que Verificamos"
        title="A ferramenta que o stand não quer que use"
        body="Cruzamos fontes públicas e oficiais para transformar sinais dispersos num relatório claro."
      />

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {sourceCards.map((card, index) => (
          <article className="flex gap-4 rounded-xl border border-border bg-card p-5 transition-shadow hover:shadow-sm" key={card.title}>
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent">
              <card.icon className="h-5 w-5 text-accent-foreground" aria-hidden />
            </div>
            <div>
              <div className="mb-1 inline-block rounded bg-secondary px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
                {sourceLabels[index] ?? "Fonte pública"}
              </div>
              <h3 className="font-semibold text-foreground">{card.title}</h3>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{card.body}</p>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

export const PricingSection = ({ navigate }: NavigationProps) => (
  <section id="precos" className="bg-secondary py-20 md:py-24" aria-labelledby="pricing-heading">
    <div className="mx-auto max-w-6xl px-4 md:px-6">
      <SectionHeader
        eyebrow="Preços"
        title="Transparente e sem surpresas"
        body="Pagamento único. Acesso permanente. Relatório completo só quando precisa."
      />
      <PricingCards navigate={navigate} />
    </div>
  </section>
);

export const PricingCards = ({ navigate }: NavigationProps) => (
  <div className="grid gap-6 md:grid-cols-2">
    {pricingCards.map((plan) => (
      <article
        className={cx(
          "relative flex flex-col rounded-xl border bg-card p-6",
          plan.featured ? "border-primary shadow-lg ring-1 ring-primary" : "border-border",
        )}
        key={plan.name}
      >
        {plan.featured && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
            <span className="rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">Mais popular</span>
          </div>
        )}

        {"tag" in plan && plan.tag && (
          <span className="mb-3 inline-block self-start rounded-full bg-warning/10 px-2.5 py-1 text-xs font-semibold text-warning">
            {plan.tag}
          </span>
        )}

        <h3 className="font-semibold text-foreground">{plan.name}</h3>
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{plan.description}</p>
        <div className="mt-4">
          <p className="text-4xl font-bold text-foreground">{plan.price}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{plan.period}</p>
        </div>

        <ul className="mt-6 flex flex-col gap-2.5" aria-label={`Funcionalidades do plano ${plan.name}`}>
          {plan.features.map((feature) => (
            <li className="flex items-start gap-2 text-sm text-muted-foreground" key={feature}>
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        <div className="mt-auto pt-6">
          <button
            className={getButtonClass(plan.variant, "md", "w-full")}
            disabled={plan.disabled}
            onClick={() => navigate(plan.target)}
          >
            {plan.cta}
          </button>
        </div>
      </article>
    ))}
  </div>
);

export const FAQSection = () => (
  <section id="faq" className="bg-background py-20 md:py-24" aria-labelledby="faq-heading">
    <div className="mx-auto max-w-3xl px-4 md:px-6">
      <SectionHeader eyebrow="FAQ" title="Perguntas Frequentes" />
      <FaqList entries={homepageFaqEntries} />
    </div>
  </section>
);

type FaqListProps = {
  entries: ReadonlyArray<[question: string, answer: string]>;
};

export const FaqList = ({ entries }: FaqListProps) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <dl className="overflow-hidden rounded-xl border border-border bg-card">
      {entries.map(([question, answer], index) => (
        <div className="border-b border-border last:border-b-0" key={question}>
          <dt>
            <button
              className="focus-ring flex w-full items-center justify-between gap-4 px-6 py-5 text-left text-sm font-medium text-foreground transition-colors hover:bg-muted/50"
              onClick={() => handleToggle(index)}
              aria-expanded={openIndex === index}
              aria-controls={`faq-answer-${index}`}
              id={`faq-question-${index}`}
            >
              <span>{question}</span>
              <ChevronDown
                className={cx("h-4 w-4 shrink-0 text-muted-foreground transition-transform", openIndex === index && "rotate-180")}
                aria-hidden
              />
            </button>
          </dt>
          <dd
            className="px-6 pb-5"
            hidden={openIndex !== index}
            id={`faq-answer-${index}`}
            role="region"
            aria-labelledby={`faq-question-${index}`}
          >
            <p className="text-sm leading-relaxed text-muted-foreground">{answer}</p>
          </dd>
        </div>
      ))}
    </dl>
  );
};

export const CTABanner = ({ navigate }: NavigationProps) => (
  <section className="bg-primary py-16 md:py-20" aria-labelledby="cta-heading">
    <div className="mx-auto max-w-4xl px-4 text-center md:px-6">
      <div className="mb-4 flex justify-center">
        <ShieldCheck className="h-10 w-10 text-primary-foreground/80" aria-hidden />
      </div>
      <h2 id="cta-heading" className="text-balance text-3xl font-bold text-primary-foreground md:text-4xl">
        Não seja a próxima vítima
      </h2>
      <p className="mt-4 text-pretty text-base leading-relaxed text-primary-foreground/80 md:text-lg">
        Verificar um stand demora menos de 10 segundos. Recuperar de uma fraude pode demorar anos.
      </p>
      <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
        <button className={getButtonClass("secondary", "lg")} onClick={() => navigate("/verificar")}>
          Verificar Stand Agora
          <ArrowRight className="h-4 w-4" aria-hidden />
        </button>
        <button className={getButtonClass("ghost", "lg", "text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground")} onClick={() => navigate("#precos")}>
          Ver Preços
        </button>
      </div>
      <p className="mt-4 text-sm text-primary-foreground/60">Pagamento único · Acesso permanente · PDF incluído</p>
    </div>
  </section>
);

export const ExampleReportPage = ({ navigate }: NavigationProps) => (
  <PageShell>
    <div className="mb-6 rounded-xl border border-warning/20 bg-warning/10 p-5 text-warning">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
          <div>
            <p className="font-semibold">Este relatório é um exemplo</p>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
              Os dados são fictícios e servem para mostrar o relatório completo, incluindo categorias desbloqueadas, recomendações,
              metodologia e leitura de risco.
            </p>
          </div>
        </div>
        <button className={getButtonClass("primary", "sm")} onClick={() => navigate("/verificar")}>
          Verificar um stand real
        </button>
      </div>
    </div>

    <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
      <ReportSummary report={exampleReport} />

      <aside className="rounded-xl border border-border bg-card p-6 shadow-sm">
        <div className="grid h-12 w-12 place-items-center rounded-lg bg-primary/10 text-primary">
          <FileText className="h-6 w-6" aria-hidden />
        </div>
        <h2 className="mt-5 text-base font-semibold text-foreground">O que recebe no relatório completo</h2>
        <ul className="mt-4 grid gap-3">
          {[
            "Score exato com explicação por categoria.",
            "Evidência e fonte associada a cada sinal.",
            "Recomendações práticas antes de pagar sinal.",
            "PDF para guardar ou partilhar.",
          ].map((item) => (
            <li className="flex gap-2 text-sm leading-relaxed text-muted-foreground" key={item}>
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden />
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <button className={getButtonClass("primary", "lg", "mt-6 w-full")} onClick={() => navigate("/verificar")}>
          Pesquisar grátis
          <ArrowRight className="h-5 w-5" aria-hidden />
        </button>
      </aside>
    </div>

    <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
      {exampleReport.categories
        .filter((category) => category.sourceId !== "managers")
        .map((category) => (
        <CategoryCard accessToken={null} category={category} exampleMode key={category.id} reportId={exampleReport.id} />
      ))}
    </div>

    <div className="mt-8 grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
      <RecommendationsPanel paid={exampleReport.paid} recommendations={exampleReport.recommendations} />
      <ManagersPanel report={exampleReport} />
    </div>

    <ReportMethodologySection report={exampleReport} />
  </PageShell>
);

type ReportPageProps = {
  navigate: (href: string) => void;
  path: string;
  reportId: string;
};

type CheckoutPageProps = NavigationProps & {
  path: string;
};

type CheckoutRouteParams = {
  sessionId: string | null;
  reportId: string | null;
  accessToken: string | null;
};

const getCheckoutRouteParams = (path: string): CheckoutRouteParams => {
  const params = new URLSearchParams(path.split("?")[1]?.split("#")[0] ?? "");

  return {
    sessionId: params.get("session_id") ?? params.get("sessionId"),
    reportId: params.get("reportId"),
    accessToken: params.get("accessToken"),
  };
};

type CheckoutStateCardProps = {
  Icon: LucideIcon;
  tone: "primary" | "success" | "warning" | "danger";
  eyebrow: string;
  title: string;
  body: string;
  children?: ReactNode;
};

const checkoutToneClasses: Record<CheckoutStateCardProps["tone"], { icon: string; surface: string }> = {
  primary: { icon: "text-primary", surface: "bg-primary/10" },
  success: { icon: "text-success", surface: "bg-success/10" },
  warning: { icon: "text-warning", surface: "bg-warning/10" },
  danger: { icon: "text-danger", surface: "bg-danger/10" },
};

export const CheckoutStateCard = ({ Icon, body, children, eyebrow, title, tone }: CheckoutStateCardProps) => {
  const classes = checkoutToneClasses[tone];

  return (
    <div className="mx-auto max-w-2xl rounded-xl border border-border bg-card p-6 shadow-sm md:p-8">
      <div className={cx("grid h-12 w-12 place-items-center rounded-lg", classes.surface)}>
        <Icon className={cx("h-6 w-6", classes.icon)} />
      </div>
      <p className="mt-5 text-sm font-semibold uppercase tracking-normal text-muted-foreground">{eyebrow}</p>
      <h1 className="mt-2 text-balance text-3xl font-bold text-foreground md:text-4xl">{title}</h1>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground md:text-base">{body}</p>
      {children && <div className="mt-6">{children}</div>}
    </div>
  );
};

const getCheckoutPendingCopy = (status: CheckoutStatus) =>
  status === "not_found"
    ? "Ainda não recebemos a confirmação do processador de pagamento. Esta página continua a verificar automaticamente."
    : "Alguns métodos, como Multibanco, podem demorar alguns minutos. Enviaremos o email de acesso assim que o pagamento ficar confirmado.";

export const CheckoutReturnPage = ({ navigate, path }: CheckoutPageProps) => {
  const params = getCheckoutRouteParams(path);
  const checkoutQuery = useQuery({
    queryKey: ["checkout-status", params.sessionId],
    queryFn: () => getCheckoutStatus(params.sessionId ?? ""),
    enabled: Boolean(params.sessionId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "paid" || status === "failed" ? false : 2500;
    },
  });
  const status = checkoutQuery.data?.status ?? "pending";
  const reportId = checkoutQuery.data?.reportId ?? params.reportId;
  const reportUrl = reportId && params.accessToken ? `/report/${reportId}?accessToken=${encodeURIComponent(params.accessToken)}&checkout=paid` : null;

  if (!params.sessionId) {
    return (
      <PageShell>
        <CheckoutStateCard
          Icon={AlertTriangle}
          body="Não recebemos a referência da sessão de checkout. Volte ao relatório ou à página de preços e tente novamente."
          eyebrow="Pagamento"
          title="Não foi possível confirmar esta sessão"
          tone="danger"
        >
          <button className={getButtonClass("primary", "lg", "w-full sm:w-auto")} onClick={() => navigate(reportId ? `/report/${reportId}` : "/pricing")}>
            Voltar
            <ArrowRight className="h-5 w-5" />
          </button>
        </CheckoutStateCard>
      </PageShell>
    );
  }

  if (checkoutQuery.isError || status === "failed") {
    return (
      <PageShell>
        <CheckoutStateCard
          Icon={AlertTriangle}
          body={
            status === "failed"
              ? "O processador de pagamento marcou esta sessão como falhada. Pode voltar e tentar com outro método."
              : (checkoutQuery.error?.message ?? "Não foi possível consultar o estado do pagamento.")
          }
          eyebrow="Relatório completo"
          title="Pagamento não confirmado"
          tone="danger"
        >
          <div className="flex flex-col gap-3 sm:flex-row">
            <button className={getButtonClass("primary", "lg", "w-full sm:w-auto")} onClick={() => navigate(reportId ? `/report/${reportId}` : "/pricing")}>
              Tentar novamente
            </button>
            {reportId && (
              <button className={getButtonClass("outline", "lg", "w-full sm:w-auto")} onClick={() => navigate(`/report/${reportId}`)}>
                Recuperar no relatório
              </button>
            )}
          </div>
        </CheckoutStateCard>
      </PageShell>
    );
  }

  if (status === "paid") {
    return (
      <PageShell>
        <CheckoutStateCard
          Icon={CheckCircle2}
          body="O pagamento foi confirmado. O relatório completo está desbloqueado e o email de acesso foi enviado ou registado para envio."
          eyebrow="Relatório completo"
          title="Pagamento confirmado"
          tone="success"
        >
          <div className="flex flex-col gap-3 sm:flex-row">
            <button className={getButtonClass("primary", "lg", "w-full sm:w-auto")} onClick={() => navigate(reportUrl ?? "/pricing")}>
              {reportUrl ? "Abrir relatório" : "Voltar aos preços"}
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </CheckoutStateCard>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <CheckoutStateCard
        Icon={Clock3}
        body={getCheckoutPendingCopy(status)}
        eyebrow="Relatório completo"
        title="A confirmar pagamento"
        tone="warning"
      >
        <div className="rounded-lg border border-border bg-secondary/70 p-4">
          <div className="flex items-center gap-3 text-sm font-semibold text-foreground">
            <RefreshCw className="h-4 w-4 animate-spin text-primary" />
            Verificação automática ativa
          </div>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Pode manter esta página aberta. Quando o estado ficar confirmado, mostramos o acesso ao relatório.
          </p>
        </div>
      </CheckoutStateCard>
    </PageShell>
  );
};

export const CheckoutCancelledPage = ({ navigate, path }: CheckoutPageProps) => {
  const params = getCheckoutRouteParams(path);
  const retryHref = params.reportId ? `/report/${params.reportId}` : "/pricing";

  return (
    <PageShell>
      <CheckoutStateCard
        Icon={X}
        body="A sessão de pagamento foi cancelada antes da confirmação. Nenhum relatório foi desbloqueado."
        eyebrow="Relatório completo"
        title="Pagamento cancelado"
        tone="warning"
      >
        <div className="flex flex-col gap-3 sm:flex-row">
          <button className={getButtonClass("primary", "lg", "w-full sm:w-auto")} onClick={() => navigate(retryHref)}>
            Voltar e tentar novamente
          </button>
          <button className={getButtonClass("outline", "lg", "w-full sm:w-auto")} onClick={() => navigate("/")}>
            Nova pesquisa
          </button>
        </div>
      </CheckoutStateCard>
    </PageShell>
  );
};

export const ReportPage = ({ navigate, path, reportId }: ReportPageProps) => {
  const turnstileConfig = useTurnstileConfig();
  const { siteKey: turnstileSiteKey, isMisconfigured: turnstileMisconfigured, isLoading: turnstileLoading, required: turnstileRequired } =
    turnstileConfig;
  const reportParams = new URLSearchParams(path.split("?")[1]?.split("#")[0] ?? "");
  const accessToken = reportParams.get("accessToken");
  const qaParam = reportParams.get("qa");
  const paymentReturned = reportParams.has("session_id");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [recoveryMessage, setRecoveryMessage] = useState("");
  const [refreshTurnstileError, setRefreshTurnstileError] = useState("");
  const [refreshTurnstileResetSignal, setRefreshTurnstileResetSignal] = useState(0);
  const [refreshTurnstileToken, setRefreshTurnstileToken] = useState("");
  const queryClient = useQueryClient();

  const reportQuery = useQuery({
    queryKey: ["report", reportId, accessToken, qaParam],
    queryFn: () => getReport(reportId, accessToken, qaParam),
    refetchInterval: (query) => {
      const status = query.state.data?.report.status;
      return status === "complete" || status === "failed" ? false : 2000;
    },
  });

  useEffect(() => {
    const grantedAccessToken = reportQuery.data?.accessToken;

    if (!grantedAccessToken || accessToken === grantedAccessToken) {
      return;
    }

    const nextPath = `/report/${reportId}?accessToken=${encodeURIComponent(grantedAccessToken)}`;
    window.history.replaceState(null, "", nextPath);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }, [accessToken, reportId, reportQuery.data?.accessToken]);

  const checkoutMutation = useMutation({
    mutationFn: createCheckout,
    onSuccess: (response) => {
      window.location.assign(response.checkoutUrl);
    },
  });

  const recoveryMutation = useMutation({
    mutationFn: recoverAccess,
    onSuccess: (response) => {
      if (response.matchedReports === 0) {
        setRecoveryMessage("Não encontrámos relatórios associados a este email.");
        return;
      }

      setRecoveryMessage(
        response.delivery === "email"
          ? "Enviámos os links de acesso para esse email."
          : response.links?.length
            ? "Modo local: links de recuperação gerados abaixo."
            : "Pedido registado. Configure o envio de email para entregar os links automaticamente.",
      );
    },
  });

  const refreshMutation = useMutation({
    mutationFn: (report: StandReport) =>
      createReport({ entityId: report.entity.id, forceRefresh: true, turnstileToken: refreshTurnstileToken || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["report", reportId, accessToken] });
    },
    onSettled: () => {
      setRefreshTurnstileToken("");
      setRefreshTurnstileResetSignal((value) => value + 1);
    },
  });

  const handleRefreshTurnstileError = useCallback(() => {
    setRefreshTurnstileToken("");
    setRefreshTurnstileError("Confirme a verificação anti-abuso antes de atualizar os dados.");
  }, []);

  const handleRefreshTurnstileVerify = useCallback((token: string) => {
    setRefreshTurnstileError("");
    setRefreshTurnstileToken(token);
  }, []);

  const pdfMutation = useMutation({
    mutationFn: async (report: StandReport) => {
      if (!accessToken) {
        throw new Error("Sem token de acesso ao relatório completo.");
      }

      const blob = await downloadReportPdf(report.id, accessToken);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${report.id}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    },
  });

  const getValidatedEmail = () => {
    if (!isValidEmail(email)) {
      setEmailError("Introduza um email válido para receber e recuperar o acesso.");
      return null;
    }

    setEmailError("");
    return normalizeEmail(email);
  };

  const handleCheckout = (report: StandReport) => {
    const normalizedEmail = getValidatedEmail();

    if (!normalizedEmail) {
      return;
    }

    checkoutMutation.mutate({ reportId: report.id, email: normalizedEmail });
  };

  const handleDownloadPdf = (report: StandReport) => {
    pdfMutation.mutate(report);
  };

  const handleRefresh = (report: StandReport) => {
    if (turnstileLoading) {
      setRefreshTurnstileError("A carregar a verificação anti-abuso...");
      return;
    }

    if (turnstileMisconfigured) {
      setRefreshTurnstileError("A verificação anti-abuso está temporariamente indisponível.");
      return;
    }

    if (turnstileRequired && !refreshTurnstileToken) {
      setRefreshTurnstileError("Confirme a verificação anti-abuso antes de atualizar os dados.");
      return;
    }

    refreshMutation.mutate(report);
  };

  const handleRecoverAccess = () => {
    const normalizedEmail = getValidatedEmail();

    if (!normalizedEmail) {
      return;
    }

    setRecoveryMessage("");
    recoveryMutation.mutate({ email: normalizedEmail });
  };

  if (reportQuery.isLoading) {
    return (
      <PageShell>
        <LoadingState label="A carregar relatório..." />
      </PageShell>
    );
  }

  if (reportQuery.isError) {
    return (
      <PageShell>
        <ErrorState message={reportQuery.error.message} />
      </PageShell>
    );
  }

  if (!reportQuery.data) {
    return (
      <PageShell>
        <ErrorState message="Relatório indisponível." />
      </PageShell>
    );
  }

  const report = reportQuery.data.report;

  if (report.status !== "complete") {
    return (
      <PageShell>
        <ReportPendingState report={report} />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <ReportSummary report={report} />

        <aside className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <TurnstileVerification
            action="refresh_report"
            resetSignal={refreshTurnstileResetSignal}
            siteKey={turnstileSiteKey}
            onError={handleRefreshTurnstileError}
            onVerify={handleRefreshTurnstileVerify}
          />
          {refreshTurnstileError && <p className="mb-4 text-sm font-semibold text-danger">{refreshTurnstileError}</p>}
          <button className={getButtonClass("outline", "md", "mb-5 w-full")} disabled={refreshMutation.isPending} onClick={() => handleRefresh(report)}>
            <RefreshCw className={cx("h-4 w-4", refreshMutation.isPending && "animate-spin")} />
            Atualizar dados
          </button>
          {refreshMutation.isError && <p className="mb-4 text-sm font-semibold text-danger">{refreshMutation.error.message}</p>}

          {report.paid ? (
            <div>
              <div className="flex items-start gap-3 rounded-lg bg-success/10 p-4 text-success">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
                <div>
                  <p className="font-semibold">
                    {paymentReturned ? "Pagamento confirmado" : "Relatório completo desbloqueado"}
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Pode descarregar o PDF, consultar a evidência e guardar este link para voltar ao relatório.
                  </p>
                </div>
              </div>
              {paymentReturned && (
                <div className="mt-4 rounded-lg border border-border bg-secondary/70 p-4">
                  <p className="text-sm font-semibold text-foreground">Guarde o acesso</p>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    Este link mantém o relatório desbloqueado. Se o perder, use a recuperação por email com o email do pagamento.
                  </p>
                </div>
              )}
              <button className={getButtonClass("primary", "lg", "mt-5 w-full")} onClick={() => handleDownloadPdf(report)}>
                <Download className="h-5 w-5" />
                Descarregar PDF
              </button>
              {pdfMutation.isError && <p className="mt-3 text-sm font-semibold text-danger">{pdfMutation.error.message}</p>}
            </div>
          ) : (
            <div>
              <div className="flex items-start gap-3">
                <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                  <Lock className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-semibold">Desbloquear relatório completo</p>
                  <p className="mt-1 text-sm text-muted-foreground">Inclui score exato, evidência por fonte, recomendações e PDF.</p>
                </div>
              </div>
              <label className="mt-5 block">
                <span className="text-sm font-medium text-muted-foreground">Email para receber acesso</span>
                <input
                  className="focus-ring mt-2 h-12 w-full rounded-md border border-input bg-background px-4 text-sm font-medium text-foreground placeholder:text-muted-foreground"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  aria-invalid={emailError ? "true" : "false"}
                  placeholder="nome@email.pt"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setEmailError("");
                    setRecoveryMessage("");
                  }}
                />
              </label>
              <button className={getButtonClass("primary", "lg", "mt-4 w-full")} disabled={checkoutMutation.isPending} onClick={() => handleCheckout(report)}>
                Desbloquear por 19,99 €
                <ArrowRight className="h-5 w-5" />
              </button>
              <button className={getButtonClass("ghost", "sm", "mt-3 w-full")} disabled={recoveryMutation.isPending} onClick={handleRecoverAccess}>
                <Mail className="h-4 w-4" />
                Recuperar acesso por email
              </button>
              {recoveryMessage && <p className="mt-3 text-sm font-semibold text-muted-foreground">{recoveryMessage}</p>}
              {recoveryMutation.data?.links && recoveryMutation.data.links.length > 0 && (
                <div className="mt-3 grid gap-2 rounded-lg border border-border bg-secondary/60 p-3">
                  {recoveryMutation.data.links.map((link) => (
                    <a className="focus-ring rounded-md text-sm font-semibold text-primary underline-offset-4 hover:underline" href={link.url} key={link.url}>
                      Abrir {link.reportId}
                    </a>
                  ))}
                </div>
              )}
              {recoveryMutation.isError && <p className="mt-3 text-sm font-semibold text-danger">{recoveryMutation.error.message}</p>}
              {emailError && <p className="mt-3 text-sm font-semibold text-danger">{emailError}</p>}
              {checkoutMutation.isError && <p className="mt-3 text-sm font-semibold text-danger">{checkoutMutation.error.message}</p>}
            </div>
          )}
        </aside>
      </div>

      <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {report.categories
          .filter((category) => category.sourceId !== "managers")
          .map((category) => (
          <CategoryCard accessToken={accessToken} category={category} key={category.id} reportId={report.id} />
        ))}
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
        <RecommendationsPanel paid={report.paid} recommendations={report.recommendations} />
        <ManagersPanel report={report} />
      </div>

      <ReportMethodologySection report={report} />
    </PageShell>
  );
};

type PageShellProps = {
  children: ReactNode;
};

export const PageShell = ({ children }: PageShellProps) => (
  <section className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10 md:py-12">{children}</section>
);

type ReportSummaryProps = {
  report: StandReport;
};

export const ReportSummary = ({ report }: ReportSummaryProps) => {
  const band = bandStyles[report.band];
  const score = report.score ?? (report.band === "Seguro" ? 86 : report.band === "Atenção" ? 68 : report.band === "Risco Elevado" ? 50 : 28);
  const Icon = band.icon;
  const caeLabel = report.entity.cae ? `CAE ${report.entity.cae}` : "CAE por confirmar";
  const ageLabel = report.entity.ageYears === null ? "antiguidade por confirmar" : `${report.entity.ageYears} anos de atividade`;
  const complianceCategory = report.categories.find((category) => category.sourceId === "automotive_compliance");
  const complianceSeverity = complianceCategory?.severity ?? "info";
  const complianceTone = severityStyles[complianceSeverity];
  const complianceLabel =
    complianceCategory?.status === "checked"
      ? "CAE compatível"
      : complianceSeverity === "warning"
        ? "Atividade requer confirmação"
        : "Atividade por confirmar";

  return (
    <section className="rounded-xl border border-border bg-card p-6 shadow-sm md:p-8">
      <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className={cx("inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-semibold", band.badge)}>
              <Icon className="h-4 w-4" />
              {band.label}
            </span>
            <span className={cx("inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-semibold", complianceTone.badge)}>
              <span className={cx("h-2 w-2 rounded-full", complianceTone.dot)} aria-hidden />
              {complianceLabel}
            </span>
          </div>
          <h1 className="mt-5 text-balance text-3xl font-bold tracking-tight text-foreground md:text-5xl">{report.entity.name}</h1>
          <p className="mt-2 font-mono text-sm font-medium text-muted-foreground">NIF {formatNif(report.entity.nif)}</p>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            {report.entity.address} · {caeLabel} · {ageLabel}
          </p>
        </div>
        <div
          className="score-gauge grid h-32 w-32 shrink-0 place-items-center rounded-full"
          style={{ "--gauge": `${score}%`, "--gauge-color": band.gauge } as CSSProperties}
          aria-label={`Score ${report.score ?? "por confirmar"} em 100`}
        >
          <div className="text-center">
            <p className="text-4xl font-bold text-foreground">{report.score ?? "—"}</p>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Score</p>
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-xl bg-secondary p-5">
        <p className="text-base font-semibold text-foreground">{report.verdict}</p>
        <p className="mt-2 text-sm text-muted-foreground">
          Atualizado em {new Date(report.checkedAt).toLocaleString("pt-PT")}. Ausência numa lista pública significa apenas
          que não encontrámos correspondência nessa consulta; não equivale a certidão oficial nem substitui aconselhamento
          jurídico.
        </p>
      </div>
    </section>
  );
};

type CategoryCardProps = {
  accessToken: string | null;
  category: ReportCategory;
  exampleMode?: boolean;
  reportId: string;
};

export const CategoryCard = ({ category, exampleMode = false }: CategoryCardProps) => {
  const Icon = category.locked ? FileLock2 : categoryIcons[category.sourceId] ?? Shield;
  const tone = getCategoryStatusStyle(category);

  return (
    <article className="relative min-h-[260px] overflow-hidden rounded-xl border border-border bg-card p-6 shadow-sm">
      <div className={cx(category.locked && "pointer-events-none opacity-50 blur-[1px]")}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent">
            <Icon className="h-5 w-5 text-accent-foreground" />
          </div>
          <span className={cx("inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold", tone.badge)}>
            <span className={cx("h-2 w-2 rounded-full", tone.dot)} aria-hidden />
            {tone.label}
          </span>
        </div>

        <h3 className="mt-6 text-base font-semibold text-foreground">{category.title}</h3>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{category.summary}</p>
        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{tone.description}</p>

        <div className="mt-4 grid gap-2">
          {category.findings.map((finding) => {
            const link = parseFindingLink(finding);

            if (link) {
              return (
                <a
                  className="rounded-md bg-secondary px-3 py-2 text-sm text-primary underline-offset-2 hover:underline"
                  href={link.href}
                  key={finding}
                  rel="noreferrer"
                  target="_blank"
                >
                  {link.label}
                  <ExternalLink className="ml-1 inline h-3.5 w-3.5" aria-hidden />
                </a>
              );
            }

            return (
              <p className="rounded-md bg-secondary px-3 py-2 text-sm text-muted-foreground" key={finding}>
                {finding}
              </p>
            );
          })}
        </div>

        <div className="mt-5 grid gap-2 border-t border-border pt-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <Database className="h-4 w-4" />
            {category.sourceName}
          </div>
          {!category.locked && exampleMode && (
            <a className={getButtonClass("outline", "sm", "w-fit")} href="/methodology">
              Ver metodologia
              <FileText className="h-4 w-4" />
            </a>
          )}
        </div>
      </div>

      {category.locked && (
        <div className="absolute inset-0 grid place-items-center bg-card/70 p-6 text-center backdrop-blur-[2px]">
          <div>
            <Lock className="mx-auto h-6 w-6 text-primary" />
            <p className="mt-2 text-sm font-semibold text-foreground">Desbloquear para ver detalhe</p>
          </div>
        </div>
      )}
    </article>
  );
};

type RecommendationsPanelProps = {
  paid: boolean;
  recommendations: StandReport["recommendations"];
};

export const RecommendationsPanel = ({ paid, recommendations }: RecommendationsPanelProps) => {
  if (!paid) {
    return (
      <section className="rounded-xl border border-border bg-card p-6 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
            <Lock className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-foreground">Recomendações bloqueadas</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              O relatório completo mostra ações concretas por severidade, incluindo o que confirmar antes de pagar sinal.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-border bg-card p-6 shadow-sm">
      <h2 className="text-base font-semibold text-foreground">Recomendações</h2>
      <div className="mt-5 grid gap-3">
        {recommendations.map((recommendation) => {
          const tone = severityStyles[recommendation.severity];

          return (
            <div className="rounded-lg bg-secondary p-4" key={recommendation.title}>
              <div className="flex items-center gap-2">
                <span className={cx("h-2 w-2 rounded-full", tone.dot)} aria-hidden />
                <p className="font-semibold text-foreground">{recommendation.title}</p>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{recommendation.body}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
};

type ManagersPanelProps = {
  report: StandReport;
};

export const ManagersPanel = ({ report }: ManagersPanelProps) => {
  const rows = getManagersPanelRows(report);

  return (
  <section className="rounded-xl border border-border bg-card p-6 shadow-sm">
    <h2 className="text-base font-semibold text-foreground">Gerentes e sócios</h2>
    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{getManagersPanelSourceLabel(report)}</p>
    <div className="mt-4 overflow-hidden rounded-lg border border-border">
      <table className="w-full text-left text-sm">
        <thead className="bg-secondary text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="px-4 py-3 font-semibold">Nome</th>
            <th className="px-4 py-3 font-semibold">Função</th>
            <th className="px-4 py-3 font-semibold">Detalhe</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.length > 0 ? (
            rows.map((row) => (
              <tr key={row.id}>
                <td className="px-4 py-3 font-medium text-foreground">{row.name}</td>
                <td className="px-4 py-3 text-muted-foreground">{row.role}</td>
                <td className="px-4 py-3 text-muted-foreground">{row.detail}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td className="px-4 py-4 text-muted-foreground" colSpan={3}>
                Gerência e titulares por confirmar nas fontes disponíveis.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </section>
  );
};

type ReportMethodologySectionProps = {
  report: StandReport;
};

const methodologySteps = [
  "Identificamos a entidade por NIF/NIPC e normalizamos os dados base disponíveis.",
  "Consultamos fontes públicas configuradas e guardamos evidência datada por fonte.",
  "Aplicamos impacto de score apenas quando a fonte devolve sinal verificável; falhas técnicas e fontes desligadas ficam neutras.",
  "Mostramos limites legais e operacionais para evitar confundir ausência de correspondência com certidão oficial.",
] as const;

const sourceStatusOrder: SourceStatus[] = ["checked", "no_data", "attention", "failed", "not_connected", "locked"];

export const ReportMethodologySection = ({ report }: ReportMethodologySectionProps) => {
  const counts = sourceStatusOrder
    .map((status) => ({
      status,
      count: report.categories.filter((category) => (category.locked ? "locked" : category.status) === status).length,
    }))
    .filter((item) => item.count > 0);

  return (
    <section className="mt-8 rounded-xl border border-border bg-card p-6 shadow-sm" aria-labelledby="report-methodology-heading">
      <div className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wider text-primary">Metodologia</p>
          <h2 id="report-methodology-heading" className="mt-2 text-2xl font-bold text-foreground">
            Como interpretar este relatório
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            StandXRAY organiza sinais públicos para apoiar a decisão. O score não é uma declaração oficial sobre a empresa,
            e cada secção deve ser lida com a respetiva fonte, data e limite de cobertura.
          </p>
        </div>

        <div className="grid gap-4">
          <div className="grid gap-2 sm:grid-cols-2">
            {counts.map(({ count, status }) => {
              const tone = sourceStatusStyles[status];

              return (
                <div className="rounded-lg border border-border bg-secondary/60 p-3" key={status}>
                  <div className="flex items-center gap-2">
                    <span className={cx("h-2 w-2 rounded-full", tone.dot)} aria-hidden />
                    <p className="text-sm font-semibold text-foreground">{tone.label}</p>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {count} secção{count === 1 ? "" : "ões"}
                  </p>
                </div>
              );
            })}
          </div>

          <ol className="grid gap-2">
            {methodologySteps.map((step, index) => (
              <li className="flex gap-3 rounded-lg bg-secondary/60 p-3 text-sm text-muted-foreground" key={step}>
                <span className="font-mono text-xs font-semibold text-primary">{String(index + 1).padStart(2, "0")}</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};

export const PricingPage = ({ navigate }: NavigationProps) => (
  <PageShell>
    <div className="max-w-3xl">
      <p className="text-sm font-semibold uppercase tracking-wider text-primary">{pricingPageIntro.eyebrow}</p>
      <h1 className="mt-3 text-balance text-4xl font-bold tracking-tight text-foreground md:text-5xl">
        {pricingPageIntro.title}
      </h1>
      <p className="mt-4 text-pretty text-base leading-relaxed text-muted-foreground md:text-lg">{pricingPageIntro.body}</p>
    </div>

    <div className="mt-10">
      <PricingCards navigate={navigate} />
    </div>

    <div className="mt-10 max-w-xl">
      <AccessRecoveryPanel />
    </div>

    <section className="mt-10 rounded-xl border border-border bg-card p-6 shadow-sm">
      <h2 className="text-base font-semibold text-foreground">Comparação rápida</h2>
      <div className="mt-5 overflow-x-auto">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="py-3 pr-4 font-semibold">Funcionalidade</th>
              <th className="px-4 py-3 font-semibold">Pesquisa</th>
              <th className="px-4 py-3 font-semibold">Completo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {comparisonRows.map(([feature, searchPlan, fullPlan]) => (
              <tr key={feature}>
                <td className="py-3 pr-4 font-medium text-foreground">{feature}</td>
                <td className="px-4 py-3 text-muted-foreground">{searchPlan}</td>
                <td className="px-4 py-3 text-muted-foreground">{fullPlan}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    <PricingTrustSection />
  </PageShell>
);

export const PricingTrustSection = () => (
  <div className="mt-10 grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
    <section className="rounded-xl border border-border bg-card p-6 shadow-sm">
      <p className="text-sm font-semibold uppercase tracking-wider text-primary">Prova social</p>
      <h2 className="mt-2 text-2xl font-bold text-foreground">Feito para decidir antes de pagar sinal</h2>
      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        {pricingProofItems.map((item) => (
          <div className="rounded-lg bg-secondary/60 p-4" key={item.label}>
            <p className="text-2xl font-bold text-primary">{item.value}</p>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{item.label}</p>
          </div>
        ))}
      </div>
      <div className="mt-6 rounded-lg border border-success/20 bg-success/10 p-4">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" aria-hidden />
          <div>
            <p className="text-sm font-semibold text-foreground">{pricingGuarantee.title}</p>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{pricingGuarantee.body}</p>
          </div>
        </div>
      </div>
    </section>

    <section className="p-1">
      <p className="text-sm font-semibold uppercase tracking-wider text-primary">Billing FAQ</p>
      <h2 className="mt-2 text-2xl font-bold text-foreground">Perguntas sobre pagamento</h2>
      <div className="mt-5">
        <FaqList entries={billingFaqEntries} />
      </div>
    </section>
  </div>
);

export const AccessRecoveryPanel = () => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [message, setMessage] = useState("");
  const recoveryMutation = useMutation({
    mutationFn: recoverAccess,
    onSuccess: (response) => {
      if (response.matchedReports === 0) {
        setMessage("Não encontrámos relatórios associados a este email.");
        return;
      }

      setMessage(
        response.delivery === "email"
          ? "Enviámos os links para o seu email."
          : response.links?.length
            ? "Modo local: links gerados abaixo."
            : "Pedido registado. Configure o envio de email para entregar os links automaticamente.",
      );
    },
  });

  const handleRecover = () => {
    if (!isValidEmail(email)) {
      setEmailError("Introduza um email válido para recuperar acesso.");
      return;
    }

    setEmailError("");
    setMessage("");
    recoveryMutation.mutate({ email: normalizeEmail(email) });
  };

  return (
    <section className="rounded-xl border border-border bg-card p-6 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-secondary text-muted-foreground">
          <Mail className="h-5 w-5" aria-hidden />
        </div>
        <div>
          <h2 className="text-base font-semibold text-foreground">Recuperar acesso</h2>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
            Introduza o email usado no pagamento para receber novamente os links dos relatórios comprados.
          </p>
        </div>
      </div>

      <label className="mt-5 block">
        <span className="text-sm font-medium text-muted-foreground">Email de compra</span>
        <input
          className="focus-ring mt-2 h-12 w-full rounded-md border border-input bg-background px-4 text-sm font-medium text-foreground placeholder:text-muted-foreground"
          type="email"
          inputMode="email"
          autoComplete="email"
          aria-invalid={emailError ? "true" : "false"}
          placeholder="nome@email.pt"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            setEmailError("");
            setMessage("");
          }}
        />
      </label>
      <button className={getButtonClass("outline", "lg", "mt-4 w-full")} disabled={recoveryMutation.isPending} onClick={handleRecover}>
        Recuperar links
      </button>
      {emailError && <p className="mt-3 text-sm font-semibold text-danger">{emailError}</p>}
      {message && <p className="mt-3 text-sm font-semibold text-muted-foreground">{message}</p>}
      {recoveryMutation.data?.links && recoveryMutation.data.links.length > 0 && (
        <div className="mt-3 grid gap-2 rounded-lg border border-border bg-secondary/60 p-3">
          {recoveryMutation.data.links.map((link) => (
            <a className="focus-ring rounded-md text-sm font-semibold text-primary underline-offset-4 hover:underline" href={link.url} key={link.url}>
              Abrir {link.reportId}
            </a>
          ))}
        </div>
      )}
      {recoveryMutation.isError && <p className="mt-3 text-sm font-semibold text-danger">{recoveryMutation.error.message}</p>}
    </section>
  );
};

export const FaqPage = ({ navigate }: NavigationProps) => (
  <PageShell>
    <div className="mx-auto max-w-3xl">
      <p className="text-sm font-semibold uppercase tracking-wider text-primary">FAQ</p>
      <h1 className="mt-3 text-balance text-4xl font-bold tracking-tight text-foreground md:text-5xl">Perguntas frequentes</h1>
      <div className="mt-8">
        <FaqList entries={homepageFaqEntries} />
      </div>
      <button className={getButtonClass("primary", "lg", "mt-8")} onClick={() => navigate("/verificar")}>
        Verificar stand
        <ArrowRight className="h-5 w-5" />
      </button>
    </div>
  </PageShell>
);

export const ContactPage = () => (
  <PageShell>
    <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr]">
      <div>
        <p className="text-sm font-semibold uppercase tracking-wider text-primary">Contacto</p>
        <h1 className="mt-3 text-balance text-4xl font-bold tracking-tight text-foreground md:text-5xl">Fale connosco</h1>
        <p className="mt-4 text-pretty text-base leading-relaxed text-muted-foreground">
          Para parcerias de dados, pedidos legais ou apoio a relatórios, use o contacto abaixo.
        </p>
      </div>
      <div className="rounded-xl border border-border bg-card p-6 shadow-sm md:p-8">
        <Mail className="h-8 w-8 text-primary" />
        <p className="mt-5 text-xl font-semibold text-foreground">contact@standxray.com</p>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          Inclua o NIF do stand e o ID do relatório quando o assunto for um resultado específico.
        </p>
      </div>
    </div>
  </PageShell>
);

export const LegalPage = () => (
  <PageShell>
    <div className="max-w-3xl">
      <p className="text-sm font-semibold uppercase tracking-wider text-primary">Legal</p>
      <h1 className="mt-3 text-balance text-4xl font-bold tracking-tight text-foreground md:text-5xl">
        Termos, privacidade e responsabilidade
      </h1>
      <div className="mt-8 grid gap-5">
        {legalSections.map(([title, body]) => (
          <article className="rounded-xl border border-border bg-card p-6 shadow-sm" key={title}>
            <h2 className="text-base font-semibold text-foreground">{title}</h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{body}</p>
          </article>
        ))}
      </div>
    </div>
  </PageShell>
);

export const MethodologyPage = ({ navigate }: NavigationProps) => (
  <PageShell>
    <div className="max-w-3xl">
      <p className="text-sm font-semibold uppercase tracking-wider text-primary">Metodologia</p>
      <h1 className="mt-3 text-balance text-4xl font-bold tracking-tight text-foreground md:text-5xl">
        Como o StandXRAY avalia sinais públicos
      </h1>
      <p className="mt-5 text-pretty text-base leading-relaxed text-muted-foreground md:text-lg">
        O relatório cruza fontes públicas disponíveis em Portugal, separa falhas técnicas de ausência de correspondência
        e só penaliza o score quando existe sinal verificável na fonte consultada.
      </p>
    </div>

    <div className="mt-10 grid gap-5 lg:grid-cols-2">
      <section className="rounded-xl border border-border bg-card p-6 shadow-sm">
        <h2 className="text-base font-semibold text-foreground">Estados por fonte</h2>
        <div className="mt-5 grid gap-3">
          {sourceStatusOrder.filter((status) => status !== "locked").map((status) => {
            const tone = sourceStatusStyles[status];

            return (
              <div className="rounded-lg bg-secondary/60 p-4" key={status}>
                <div className="flex items-center gap-2">
                  <span className={cx("h-2 w-2 rounded-full", tone.dot)} aria-hidden />
                  <p className="font-semibold text-foreground">{tone.label}</p>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{tone.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="rounded-xl border border-border bg-card p-6 shadow-sm">
        <h2 className="text-base font-semibold text-foreground">Limites importantes</h2>
        <ul className="mt-5 grid gap-3">
          {[
            "Ausência em listas públicas não equivale a certidão oficial de não dívida, não insolvência ou inexistência de processos.",
            "Livro de Reclamações é usado apenas como fonte pública agregada; conteúdos privados e histórico por entidade não são acedidos.",
            "Publicações MJ e sócios/gerentes ficam neutros quando MJ.ENGINO.PT não está configurado ou não devolve dados estruturados.",
            "Pesquisa por nome, como em citações editais, pode incluir homónimos e deve ser confirmada manualmente no portal de origem.",
          ].map((item) => (
            <li className="flex gap-3 rounded-lg bg-secondary/60 p-4 text-sm leading-relaxed text-muted-foreground" key={item}>
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>

    <section className="mt-8 rounded-xl border border-border bg-card p-6 shadow-sm">
      <h2 className="text-base font-semibold text-foreground">Fontes cobertas nesta fase</h2>
      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {sourceCards.map((card) => (
          <article className="rounded-lg border border-border bg-secondary/60 p-4" key={card.title}>
            <card.icon className="h-5 w-5 text-primary" aria-hidden />
            <h3 className="mt-3 text-sm font-semibold text-foreground">{card.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{card.body}</p>
          </article>
        ))}
      </div>
    </section>

    <button className={getButtonClass("primary", "lg", "mt-8")} onClick={() => navigate("/verificar")}>
      Verificar um stand
      <ArrowRight className="h-5 w-5" />
    </button>
  </PageShell>
);

type AccountDeferredPageProps = NavigationProps & {
  mode: "login" | "register";
};

export const AccountDeferredPage = ({ mode, navigate }: AccountDeferredPageProps) => {
  const isLogin = mode === "login";

  return (
    <PageShell>
      <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wider text-primary">{isLogin ? "Entrar" : "Registar"}</p>
          <h1 className="mt-3 text-balance text-4xl font-bold tracking-tight text-foreground md:text-5xl">
            {isLogin ? "Acesso por email, sem palavra-passe" : "Ainda não precisa de criar conta"}
          </h1>
          <p className="mt-5 text-pretty text-base leading-relaxed text-muted-foreground md:text-lg">
            {isLogin
              ? "Nesta fase, os relatórios ficam associados ao email do pagamento. Recupere os links aqui sem criar uma conta."
              : "O StandXRAY continua focado em pesquisa rápida por NIF/NIPC. Só pedimos email quando compra relatórios."}
          </p>
          <div className="mt-6 grid gap-3">
            {[
              "Pesquisa gratuita sem conta.",
              "Relatórios pagos recuperáveis por email.",
              "Pagamento único para desbloquear cada relatório completo.",
            ].map((item) => (
              <div className="flex gap-3 rounded-lg bg-secondary/60 p-4 text-sm text-muted-foreground" key={item}>
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

        {isLogin ? (
          <AccessRecoveryPanel />
        ) : (
          <section className="rounded-xl border border-border bg-card p-6 shadow-sm">
            <h2 className="text-base font-semibold text-foreground">Comece pela verificação</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Quando o produto exigir contas, esta rota passa a ter registo completo. Por agora, o fluxo mais rápido é pesquisar
              por NIF/NIPC e guardar o acesso recebido por email.
            </p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <button className={getButtonClass("primary", "lg", "w-full")} onClick={() => navigate("/verificar")}>
                Pesquisar grátis
              </button>
              <button className={getButtonClass("outline", "lg", "w-full")} onClick={() => navigate("/pricing")}>
                Ver preços
              </button>
            </div>
          </section>
        )}
      </div>
    </PageShell>
  );
};

type SectionHeaderProps = {
  eyebrow: string;
  title: string;
  body?: string;
};

export const SectionHeader = ({ body, eyebrow, title }: SectionHeaderProps) => (
  <div className="mb-12 text-center">
    <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-primary">{eyebrow}</p>
    <h2 className="text-balance text-3xl font-bold text-foreground md:text-4xl">{title}</h2>
    {body && <p className="mx-auto mt-4 max-w-2xl text-pretty text-base leading-relaxed text-muted-foreground md:text-lg">{body}</p>}
  </div>
);

export const NotFoundPage = ({ navigate }: NavigationProps) => (
  <PageShell>
    <div className="mx-auto max-w-xl rounded-xl border border-border bg-card p-8 text-center shadow-sm">
      <Shield className="mx-auto h-10 w-10 text-primary" />
      <h1 className="mt-4 text-2xl font-bold text-foreground">Página não encontrada</h1>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
        O endereço não corresponde a uma página disponível nesta versão do StandXRAY.
      </p>
      <button className={getButtonClass("primary", "lg", "mt-6")} onClick={() => navigate("/")}>
        Voltar ao início
      </button>
    </div>
  </PageShell>
);

type FooterProps = NavigationProps;

export const Footer = ({ navigate }: FooterProps) => {
  const handleNavigate = (href: string) => {
    const isHomeAnchor = href.startsWith("#");
    const isOnHomepage = window.location.pathname === "/" || window.location.pathname === "/verificar";
    navigate(isHomeAnchor && !isOnHomepage ? `/${href}` : href);
  };

  return (
    <footer className="border-t border-border bg-card" role="contentinfo">
      <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
        <div className="grid gap-10 md:grid-cols-4">
          <div>
            <NavLink href="/" navigate={(h) => handleNavigate(h)} className="focus-ring flex items-center gap-2 rounded-md text-left text-foreground">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
                <ShieldCheck className="h-5 w-5" aria-hidden />
              </span>
              <span className="text-xl font-bold tracking-tight">
                Stand<span className="text-primary">XRAY</span>
              </span>
            </NavLink>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              A ferramenta de verificação de stands automóveis em Portugal. Dados públicos, score de risco e recomendações
              concretas.
            </p>
          </div>

          {Object.entries(footerLinkGroups).map(([section, items]) => (
            <div key={section}>
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-foreground">{section}</h3>
              <ul className="flex flex-col gap-2.5">
                {items.map((item) => (
                  <li key={item.label}>
                    <NavLink
                      href={item.href.startsWith("#") ? `/${item.href}` : item.href}
                      navigate={(h) => handleNavigate(item.href)}
                      className="focus-ring rounded-sm text-left text-sm text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {item.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-col items-center justify-between gap-4 border-t border-border pt-8 md:flex-row">
          <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} StandXRAY. Todos os direitos reservados.</p>
          <p className="text-xs text-muted-foreground">Os relatórios são informativos e não constituem aconselhamento jurídico.</p>
        </div>
      </div>
    </footer>
  );
};
